import React, { useEffect, useMemo, useState } from 'react';
import DataTable from './DataTable';
import GeneNetworkGraph from './GeneNetworkGraph';
import './ResultsModal.css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// jobtype organizers
import { organizeGene } from './jobtypes/gene';
import { organizeMultiVariant } from './jobtypes/multiVariant';
import { organizeMultiSvCnv } from './jobtypes/multiSvCnv';
import { organizeSingleVariant } from './jobtypes/singleVariant';
import { organizeSingleSvCnv } from './jobtypes/singleSvCnv';
import { getJobTypeMeta, getJobTypeKey, formatJobTypeLabel } from './jobTypeMeta';

const ResultsModal = ({ isOpen, onClose, data }) => {





  const [analysisType, setAnalysisType] = useState('');
  const [organized, setOrganized] = useState({});
  const [renderers, setRenderers] = useState({});
  const [activeTab, setActiveTab] = useState('');
  const [activeSub, setActiveSub] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [externalLinks, setExternalLinks] = useState([]);

  const files = data?.files || [];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getFileTypeInfo = (item) => {
    const meta = getJobTypeMeta(item?.job_type);
    if (meta) {
      return { icon: meta.icon, cssClass: meta.cssClass || '', label: meta.label };
    }

    const filename = (item?.original_files || '').toLowerCase();
    if (filename.includes('vcf') || filename.includes('.vcf.gz')) {
      return { icon: 'fa-file-medical-alt', cssClass: 'vcf', label: 'VCF File' };
    }
    if (
      filename.includes('chr') &&
      (filename.includes('_del') || filename.includes('_dup') || filename.match(/\d+_[atcg]_[atcg]/i))
    ) {
      return { icon: 'fa-dna', cssClass: 'variant', label: 'Variant' };
    }
    if (/^[a-z0-9._-]+$/i.test(item?.original_files || '') || filename.includes('gene')) {
      return { icon: 'fa-dna', cssClass: 'gene', label: 'Gene' };
    }
    return { icon: 'fa-file-alt', cssClass: '', label: 'Analysis' };
  };
const [liveFiles, setLiveFiles] = useState([]); // {name, data} from polling

const appendLiveFiles = React.useCallback((newOnes = []) => {
  setLiveFiles(prev => {
    const map = new Map(prev.map(f => [f.name, f]));
    for (const f of newOnes) {
      if (f?.name && (typeof f.data === 'string')) map.set(f.name, f);
    }
    return Array.from(map.values());
  });
}, []);
const allFiles = useMemo(() => {
  const base = files || [];
  const seen = new Set(base.map(f => f.name));
  const extras = (liveFiles || []).filter(f => f?.name && !seen.has(f.name));
  return [...base, ...extras];
}, [files, liveFiles]);
  // --- detection owned by modal ---
  const detectGene = (f) => {
    const names = f.map((x) => x?.name || '');
    return (
      names.some((n) => /ClinVarPLP-Lollipop-.*\.(html|json)$/i.test(n)) ||
      names.some((n) => /-(clinvar|clingen|gencc|dgidb|network|eqtl_.*|sqtl_.*|pqtl_.*)\.txt$/i.test(n)) ||
      names.some((n) => n.includes('gene_output'))
    );
  };

  const detectMultiVariant = (f) => {
    const names = f.map((x) => x?.name || '');
    return (
      names.some((n) => /(?:^|\/)[^/]+\.priorisation\.txt$/i.test(n)) ||
      names.some((n) => /\.nodup(\.|_)/i.test(n)) ||
      names.some((n) => /GO-All(\.txt|-count\.txt|-Enrich\.txt)$/i.test(n)) ||
      names.some((n) => /KEGG-All(\.txt|-count\.txt|-Enrich\.txt)$/i.test(n)) ||
      names.some((n) => /Exomiser_(PHENIX_PRIORITY|REMM)\.(json|html|genes\.tsv|variants\.tsv)$/i.test(n)) ||
      names.some((n) => /Figures\/ideogram\.png$/i.test(n)) ||
      names.some((n) => /SNP[_-]?density\.(png|jpe?g)$/i.test(n))
    );
  };

  const detectMultiSvCnv = (f) => {
    const names = f.map((x) => x?.name || '');
    return (
      names.some((n) => /\.cnv\.annotated\.tsv$/i.test(n)) ||
      names.some((n) => /_vcf2circos\.html$/i.test(n)) ||
      names.some((n) => /\.short\.vcf$/i.test(n))
    );
  };

  const detectSingleSvCnv = (f) => {
    const names = f.map((x) => x?.name || '');
    return (
      names.some((n) => /_(DEL|DUP|INS|INV|CNV)\.annotated\.tsv$/i.test(n)) ||
      names.some((n) => /(?:^|\/)(?!.*cnv\.annotated)[^/]*single.*\.tsv$/i.test(n))
    );
  };

  const detectSingleVariant = (f) => {
    const names = f.map((x) => x?.name || '');
    return (
      names.some((n) => /Lollipop\.html$/i.test(n)) ||
      names.some((n) => /AFMap\.png$/i.test(n)) ||
      (names.some((n) => /\.(eqtl_catalog|eqtl_gtex|gwas|pgx|pqtl)\.txt$/i.test(n)) &&
       !names.some((n) => /\.priorisation\.txt$/i.test(n)))
    );
  };

  const jobTypeKey = useMemo(() => getJobTypeKey(data?.job_type), [data?.job_type]);
  const infoType = useMemo(() => getFileTypeInfo(data || {}), [data]);

  useEffect(() => {
    if (!isOpen || !files.length) return;

    const setAnalysis = (key, organizer) => {
      if (!key || typeof organizer !== 'function') return false;
      const { organized: org, renderers: rnd, initial, externalLinks: links } = organizer();
      const label = getJobTypeMeta(key)?.label || formatJobTypeLabel(key) || 'Analysis';

      setAnalysisType(label);
      setOrganized(org || {});
      setRenderers(rnd || {});
      setExternalLinks(links || []);

      const firstTab = initial?.tab || Object.keys(org || {})[0] || '';
      setActiveTab(firstTab);
      const firstSub = initial?.sub || (org?.[firstTab]?.tables?.[0]?.name || '');
      setActiveSub(firstSub);
      return true;
    };

    const resolvedKey =
      jobTypeKey ||
      (detectGene(files) ? 'gene' :
        detectSingleVariant(files) ? 'single-variant' :
        detectSingleSvCnv(files) ? 'single-sv-cnv' :
        detectMultiSvCnv(files) ? 'multi-sv-cnv' :
        detectMultiVariant(files) ? 'multi-variant' :
        null);

    const handled = (() => {
      switch (resolvedKey) {
        case 'gene':
          return setAnalysis('gene', () => organizeGene(files, data));
        case 'single-variant':
          return setAnalysis('single-variant', () => organizeSingleVariant(files, data));
        case 'single-sv-cnv':
          return setAnalysis('single-sv-cnv', () => organizeSingleSvCnv(files, data));
        case 'multi-sv-cnv':
          return setAnalysis('multi-sv-cnv', () => organizeMultiSvCnv(files, data));
        case 'multi-variant':
          return setAnalysis('multi-variant', () => organizeMultiVariant(files, data));
        default:
          return false;
      }
    })();

    if (!handled) {
      setAnalysisType('Unsupported');
      setOrganized({});
      setRenderers({});
      setActiveTab('');
      setActiveSub('');
    }
  }, [isOpen, files, data, jobTypeKey]);

  const tabs = useMemo(() => Object.keys(organized || {}), [organized]);
  const section = organized?.[activeTab];

  const closeNow = () => {
    setIsClosing(true);
    setTimeout(() => { setIsClosing(false); onClose(); }, 300);
  };

  const handleExportAll = async () => {
if (!allFiles || allFiles.length === 0) {
  alert('No files to export');
  return;
}

    try {
      const zip = new JSZip();

      // Add all files to zip
for (const file of allFiles) {
  if (file?.data) {
    const isBase64Image =
      (file.contentType && file.contentType.startsWith('image/')) ||
      /\.(png|jpe?g|gif|svg)$/i.test(file.name);

    if (isBase64Image) {
      zip.file(file.name, file.data, { base64: true });
    } else {
      zip.file(file.name, file.data);
    }
  } else if (file?.url) {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      zip.file(file.name, blob);
    } catch (err) {
      console.error(`Failed to fetch ${file.name}:`, err);
    }
  }
}


      // Generate zip file
      const content = await zip.generateAsync({ type: 'blob' });

      // Create filename from original file or UUID
      const baseName = data.original_files || data.input_text || data.uuid?.substring(0, 8) || 'analysis';
      const zipName = `${baseName}_results.zip`;

      // Download
      saveAs(content, zipName);
    } catch (error) {
      console.error('Error creating zip file:', error);
      alert('Failed to export files. Please try again.');
    }
  };

  if (!isOpen) return null;

  const isLoading = data?.isLoading;

  return (
    <div className={`WSNG-modal-overlay ${isClosing ? 'closing' : ''}`} onClick={closeNow}>
      <div className="WSNG-modal" onClick={(e)=>e.stopPropagation()}>
        <div className="WSNG-modal-header">
          <h2>{analysisType} Analysis Results</h2>
          <div className="WSNG-modal-header-actions">
            <button className="WSNG-export-all-button" onClick={handleExportAll} title="Export all files" disabled={isLoading}>
              <i className="fas fa-download" />
              <span>Export All</span>
            </button>
            <button className="WSNG-close-button" onClick={closeNow}><i className="fas fa-times"/></button>
          </div>
        </div>

        <div className="WSNG-modal-content-m">
          {isLoading ? (
            <div className="WSNG-loading-container">
              <i className="fas fa-circle-notch fa-spin"></i>
            </div>
          ) : (
          <div className="WSNG-analysis-container">
            {/* Left: info + tabs */}
            <div className="WSNG-left-sidebar">
              <div className="WSNG-upload-info-header">
                <div className="WSNG-upload-info-card">
                  <div className="WSNG-info-icon">
                    <i className={`fas ${infoType?.icon || 'fa-file-alt'}`} />
                  </div>
                  <div className="WSNG-info-details">
                    <div className="WSNG-info-title">
                      {data.original_files || data.input_text || ''}
                    </div>
                    {data.notes && <div className="WSNG-info-notes">{data.notes}</div>}
                    <div className="WSNG-info-meta">
                      <span className="WSNG-info-uuid">ID: {data.uuid?.substring(0, 8)}</span>
<span className="WSNG-info-files">{allFiles.length} files</span>
                      <span className="WSNG-info-date">{formatDate(data.upload_date)}</span>
                    </div>
                  </div>
                </div>
                {externalLinks.length > 0 && (
                  <div className="WSNG-info-links">
                    {externalLinks.map((link, i) => (
                      <a key={i} href={link.url} target="_blank" rel="noreferrer" className="WSNG-info-link">
                        {link.label} <i className="fas fa-external-link-alt" />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="WSNG-tab-navigation">
                {tabs.map((t)=>(
                  <button
                    key={t}
                    className={`WSNG-tab-btn ${activeTab===t?'active':''}`}
                    onClick={()=>{
                      setActiveTab(t);
                      const ft = (organized?.[t]?.tables||[])[0]?.name;
                      if (ft) setActiveSub(ft);
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: content */}
            <div className="WSNG-content-area">
              {activeTab && section ? (
                <div className="WSNG-table-content">
                  {renderers[activeTab] ? (
                    (() => {
const Renderer = renderers[activeTab];
return (
  <Renderer
    section={section}
    activeSub={activeSub}
    files={files}
    jobData={data}
    setActiveSub={setActiveSub}
    onNewRunFiles={appendLiveFiles}   // <-- NEW
  />
);

                    })()
                  ) : section.tables?.length ? (
                    <DataTable
                      data={(section.tables.find(t=>t.name===activeSub)||{}).data || ''}
                      title={activeSub}
                      availableTables={section.tables.map(t=>({ ...t, active: t.name === activeSub }))}
                      onTableSwitch={(t)=>setActiveSub(t.name)}
                    />
                  ) : (
                    <div className="WSNG-no-analysis">No analysis data available.</div>
                  )}
                </div>
              ) : (
                <div className="WSNG-no-analysis">
                  {analysisType === 'Unsupported' ? 'This job type is not yet supported in this build.' : 'Select a category to view analysis results.'}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsModal;
